import React, { createContext, useContext, useState, useCallback } from 'react';
import type { KeywordEvent } from '../api/keywordsApi';

interface EventsCacheData {
  events: KeywordEvent[];
  totalCount: number;
  hasMore: boolean;
  currentPage: number;
  searchValue: string;
  lastFetchTime: number;
}

interface EventsCacheContextValue {
  getCachedEvents: (keywordIds: string[], includePast: boolean) => EventsCacheData | null;
  setCachedEvents: (keywordIds: string[], includePast: boolean, data: EventsCacheData) => void;
  clearCache: () => void;
}

const EventsCacheContext = createContext<EventsCacheContextValue | null>(null);

// Create a cache key from the parameters
const getCacheKey = (keywordIds: string[], includePast: boolean): string => {
  return `${keywordIds.sort().join(',')}_${includePast}`;
};

// Cache expiry time (5 minutes)
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

export const EventsCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Map<string, EventsCacheData>>(new Map());

  const getCachedEvents = useCallback((keywordIds: string[], includePast: boolean): EventsCacheData | null => {
    const key = getCacheKey(keywordIds, includePast);
    const cached = cache.get(key);
    
    if (!cached) return null;
    
    // Check if cache is expired
    const now = Date.now();
    if (now - cached.lastFetchTime > CACHE_EXPIRY_MS) {
      // Remove expired cache
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(key);
        return newCache;
      });
      return null;
    }
    
    return cached;
  }, [cache]);

  const setCachedEvents = useCallback((keywordIds: string[], includePast: boolean, data: EventsCacheData) => {
    const key = getCacheKey(keywordIds, includePast);
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.set(key, {
        ...data,
        lastFetchTime: Date.now()
      });
      return newCache;
    });
  }, []);

  const clearCache = useCallback(() => {
    setCache(new Map());
  }, []);

  return (
    <EventsCacheContext.Provider value={{ getCachedEvents, setCachedEvents, clearCache }}>
      {children}
    </EventsCacheContext.Provider>
  );
};

export const useEventsCache = () => {
  const context = useContext(EventsCacheContext);
  if (!context) {
    throw new Error('useEventsCache must be used within EventsCacheProvider');
  }
  return context;
};